import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Save, Trash2, UploadCloud, Trophy, RefreshCw, Search, UserPlus } from "lucide-react";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";

// The 10 test fields in the same order + units as the main scoring form.
const TESTS = [
  { key: "deadlift_kg",         label: "DEADLIFT (1RM)",       unit: "kg" },
  { key: "bench_kg",            label: "BENCH PRESS (1RM)",    unit: "kg" },
  { key: "forty_yard_sec",      label: "40-YARD DASH",         unit: "s"  },
  { key: "shuttle_5_10_5_sec",  label: "5-10-5 SHUTTLE",       unit: "s"  },
  { key: "vertical_jump_cm",    label: "VERTICAL JUMP",        unit: "cm" },
  { key: "broad_jump_cm",       label: "BROAD JUMP",           unit: "cm" },
  { key: "five_min_shuttle_m",  label: "5-MIN SHUTTLE",        unit: "m"  },
  { key: "mile_sec",            label: "MILE RUN",             unit: "s"  },
  { key: "dead_hang_sec",       label: "DEAD HANG",            unit: "s"  },
  { key: "single_leg_stand_sec",label: "SINGLE-LEG STAND",     unit: "s"  },
];

const emptyForm = () => ({
  height_cm: "", weight_kg: "",
  ...Object.fromEntries(TESTS.map((t) => [t.key, ""])),
});

/**
 * Admin scoring interface for a single event. Fully staged — nothing here
 * touches an athlete's official current_gps or score history until the
 * Admin taps UPLOAD EVENT SCORES (which fires POST /admin/events/{id}/commit).
 */
export default function EventScoringModal({ event, onClose, hideCommit = false }) {
  const [data, setData] = useState(null); // {event, roster, counts}
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Full-database hits from /api/admin/search — used to add non-roster athletes.
  const [globalHits, setGlobalHits] = useState([]);
  const [globalBusy, setGlobalBusy] = useState(false);
  const [adding, setAdding] = useState(null); // user_id being added

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/events/${event.id}/scoring`);
      setData(data);
      // If no selection yet, pick the first unscored athlete (else first row).
      setSelectedUserId((prev) => {
        if (prev && data.roster.some((r) => r.user.id === prev)) return prev;
        const unscored = data.roster.find((r) => !r.staged);
        return (unscored || data.roster[0])?.user.id || null;
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  // Selected roster row.
  const selected = useMemo(
    () => data?.roster.find((r) => r.user.id === selectedUserId) || null,
    [data, selectedUserId],
  );

  // Search filter — matches name, email, or public_id (case-insensitive).
  const filteredRoster = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const rows = data?.roster || [];
    if (!q) return rows;
    return rows.filter((r) => {
      const u = r.user || {};
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.public_id || "").toLowerCase().includes(q)
      );
    });
  }, [data, searchQuery]);

  // When the admin searches, ALSO hit the global admin search so they can
  // pull any athlete from the full GPS user base into the event roster.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setGlobalHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setGlobalBusy(true);
      try {
        const { data: res } = await api.get(`/admin/search`, { params: { q } });
        if (!cancelled) setGlobalHits(res.users || []);
      } catch {
        if (!cancelled) setGlobalHits([]);
      } finally {
        if (!cancelled) setGlobalBusy(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQuery]);

  // Roster ids we already have — used to filter out globalHits.
  const rosterIds = useMemo(
    () => new Set((data?.roster || []).map((r) => r.user.id)),
    [data],
  );
  const externalHits = useMemo(
    () => globalHits.filter((u) => !rosterIds.has(u.id) && u.role !== "admin"),
    [globalHits, rosterIds],
  );

  const addAthleteToRoster = async (u) => {
    setAdding(u.id);
    try {
      await api.post(`/admin/events/${event.id}/interest/${u.id}`);
      toast.success(`${u.name}: added to event`);
      setSearchQuery("");
      setGlobalHits([]);
      await load();
      setSelectedUserId(u.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAdding(null);
    }
  };

  const removeAthleteFromRoster = async (row, ev) => {
    ev?.stopPropagation?.();
    const u = row.user;
    const isCommitted = row.staged?.committed;
    const hasStaged = !!row.staged && !isCommitted;
    const msg = isCommitted
      ? `Remove ${u.name} from this event's roster? Their COMMITTED score row in the athlete's history will be KEPT — this only removes them from the event roster. Continue?`
      : hasStaged
        ? `Remove ${u.name} from this event? Their staged (not-yet-uploaded) score for this event will be permanently discarded. This cannot be undone.`
        : `Remove ${u.name} from this event's roster? They have no staged score yet.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/events/${event.id}/interest/${u.id}`);
      toast.success(`${u.name}: removed from event`);
      if (selectedUserId === u.id) setSelectedUserId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  // When the selected athlete changes, hydrate the form with their staged
  // values (if any) or preload their last-known height/weight from profile.
  useEffect(() => {
    if (!selected) { setForm(emptyForm()); return; }
    const staged = selected.staged;
    if (staged) {
      setForm({
        height_cm: staged.height_cm ?? "",
        weight_kg: staged.weight_kg ?? "",
        ...Object.fromEntries(TESTS.map((t) => [t.key, staged.raw?.[t.key] ?? ""])),
      });
    } else {
      setForm({
        ...emptyForm(),
        height_cm: selected.user.height_cm ?? "",
        weight_kg: selected.user.weight_kg ?? "",
      });
    }
  }, [selected?.user.id]); // eslint-disable-line

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveScore = async () => {
    if (!selected) return;
    // Build payload, dropping empty strings so the backend treats them as absent.
    const body = { user_id: selected.user.id };
    const h = parseFloat(form.height_cm);
    const w = parseFloat(form.weight_kg);
    if (Number.isNaN(h) || Number.isNaN(w)) {
      toast.error("Height and weight are required");
      return;
    }
    body.height_cm = h;
    body.weight_kg = w;
    for (const t of TESTS) {
      const v = form[t.key];
      if (v === "" || v == null) continue;
      const parsed = parseFloat(v);
      if (Number.isNaN(parsed)) {
        toast.error(`${t.label}: invalid number`);
        return;
      }
      body[t.key] = parsed;
    }
    setBusy(true);
    try {
      await api.post(`/admin/events/${event.id}/scores`, body);
      toast.success(`${selected.user.name}: staged`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteScore = async () => {
    if (!selected?.staged) return;
    if (selected.staged.committed) {
      toast.error("Committed scores can't be removed from here");
      return;
    }
    if (!window.confirm(`Clear staged score for ${selected.user.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/events/${event.id}/scores/${selected.user.id}`);
      toast.success(`${selected.user.name}: staged score cleared`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const commitAll = async () => {
    if (!data) return;
    const pending = data.counts?.pending_commit || 0;
    if (!pending) {
      toast.error("Nothing to upload — no pending staged scores");
      return;
    }
    if (!window.confirm(`Push ${pending} staged score(s) to official GPS history? This cannot be undone from here.`)) return;
    setCommitting(true);
    try {
      const { data: res } = await api.post(`/admin/events/${event.id}/commit`);
      toast.success(`Uploaded ${res.committed} score(s)`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCommitting(false);
    }
  };

  const counts = data?.counts || { interested: 0, staged: 0, committed: 0, pending_commit: 0 };
  const staged = selected?.staged;
  const committedLocked = !!staged?.committed;
  // UPLOAD guard: every athlete on the roster must be either (a) already
  // committed or (b) staged with ALL 10 tests filled. This prevents the
  // admin from pushing partial data into official history.
  const rosterComplete = useMemo(() => {
    const rows = data?.roster || [];
    if (rows.length === 0) return false;
    return rows.every((r) => {
      if (!r.staged) return false;
      if (r.staged.committed) return true;
      const filled = Object.keys(r.staged.raw || {}).length;
      return filled >= TESTS.length;
    });
  }, [data]);
  const canUpload = rosterComplete && counts.pending_commit > 0;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="admin-event-scoring-modal"
    >
      <div className="min-h-full grid place-items-start justify-center p-4 pt-24 pb-10">
        <GlassPanel
          className="w-full max-w-6xl relative"
          scanline
          onClick={(ev) => ev.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-mute hover:text-white z-10"
            aria-label="Close"
            data-testid="admin-scoring-close"
          >
            <X size={18} />
          </button>

          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-white/10">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="gps-label">EVENT SCORING · STAGED</div>
                <h3 className="gps-heading text-2xl text-white">{event.name.toUpperCase()}</h3>
                <div className="text-mute gps-mono text-[10px] tracking-[0.2em] mt-2">
                  INTERESTED {counts.interested} · STAGED {counts.staged} · COMMITTED {counts.committed} · PENDING {counts.pending_commit}
                </div>
                {!hideCommit && !rosterComplete && counts.interested > 0 && (
                  <div
                    className="mt-1 gps-mono text-[10px] tracking-[0.15em] text-orange-300"
                    data-testid="admin-scoring-upload-hint"
                  >
                    ⚠ UPLOAD LOCKED · EVERY ATHLETE NEEDS ALL 10 TESTS SAVED
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <button
                  onClick={load}
                  className="gps-btn ghost"
                  data-testid="admin-scoring-refresh"
                  title="Reload roster"
                >
                  <RefreshCw size={12} /> REFRESH
                </button>
                {!hideCommit && (
                  <button
                    onClick={commitAll}
                    className="gps-btn"
                    disabled={committing || !canUpload}
                    data-testid="admin-scoring-commit"
                    title={
                      rosterComplete
                        ? "Push all staged scores into athletes' official GPS history"
                        : "Every athlete must have all 10 tests entered before you can upload"
                    }
                  >
                    <UploadCloud size={14} />
                    {committing ? "UPLOADING…" : `UPLOAD EVENT SCORES${counts.pending_commit ? ` (${counts.pending_commit})` : ""}`}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Body — natural height, page scroll only */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4">
          {/* Roster */}
          <div className="flex flex-col">
            <div className="mb-2 relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute pointer-events-none" />
              <input
                className="gps-input w-full pl-8"
                placeholder="SEARCH ROSTER OR GPS DATABASE…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="admin-scoring-search"
              />
            </div>
            <div className="flex items-center gap-3 text-mute gps-mono text-[10px] tracking-[0.2em] mb-2">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> COMPLETE</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block" /> PARTIAL</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block" /> NONE</span>
            </div>
            <div className="border border-white/10 rounded-sm">
              {filteredRoster.length === 0 && externalHits.length === 0 && (
                <div className="p-6 text-center text-mute gps-mono text-xs tracking-[0.2em]" data-testid="admin-scoring-empty">
                  {(data?.roster || []).length === 0 && !searchQuery
                    ? "NO ATHLETES YET · SEARCH TO ADD ONE"
                    : (globalBusy ? "SEARCHING…" : "NO MATCHES")}
                </div>
              )}
              {filteredRoster.map((r) => {
                const active = r.user.id === selectedUserId;
                const stagedGps = r.staged?.result?.gps;
                const filled = r.staged ? Object.keys(r.staged.raw || {}).length : 0;
                let dotClass = "bg-rose-500";
                let dotLabel = "NONE";
                if (filled >= TESTS.length) { dotClass = "bg-emerald-400"; dotLabel = "COMPLETE"; }
                else if (filled > 0)        { dotClass = "bg-orange-400";  dotLabel = "PARTIAL"; }
                return (
                  <div
                    key={r.user.id}
                    className={`group w-full border-b border-white/5 transition-colors flex items-stretch ${
                      active ? "bg-cyan-400/[0.10]" : "hover:bg-white/[0.04]"
                    }`}
                    data-testid={`admin-scoring-row-${r.user.id}`}
                    data-completion={dotLabel.toLowerCase()}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedUserId(r.user.id)}
                      className="flex-1 text-left p-3 min-w-0"
                      data-testid={`admin-scoring-select-${r.user.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-2.5 h-2.5 rounded-full inline-block ${dotClass}`}
                          title={`${filled}/${TESTS.length} tests · ${dotLabel}`}
                          data-testid={`admin-scoring-dot-${r.user.id}`}
                        />
                        <span className="text-white text-sm truncate">{r.user.name}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1 gps-mono text-[10px] tracking-[0.15em]">
                        <span className="text-cyan-400">{r.user.public_id || "—"}</span>
                        {r.staged ? (
                          r.staged.committed ? (
                            <span className="text-emerald-400">COMMITTED</span>
                          ) : (
                            <span className={filled >= TESTS.length ? "text-emerald-300" : "text-orange-300"}>
                              {filled}/{TESTS.length} · {Number(stagedGps).toFixed(1)}
                            </span>
                          )
                        ) : (
                          <span className="text-rose-400">UNSCORED</span>
                        )}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(ev) => removeAthleteFromRoster(r, ev)}
                      className="px-2 text-mute hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      title={`Remove ${r.user.name} from this event`}
                      aria-label={`Remove ${r.user.name}`}
                      data-testid={`admin-scoring-remove-${r.user.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
              {externalHits.length > 0 && (
                <div
                  className="px-3 py-2 gps-mono text-[10px] text-mute tracking-[0.25em] bg-white/[0.03] border-t border-white/10"
                  data-testid="admin-scoring-external-header"
                >
                  ADD FROM GPS DATABASE · {externalHits.length}
                </div>
              )}
              {externalHits.map((u) => (
                <div
                  key={u.id}
                  className="p-3 border-b border-white/5 hover:bg-white/[0.04] flex items-center justify-between gap-3"
                  data-testid={`admin-scoring-external-${u.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-white text-sm truncate">{u.name}</div>
                    <div className="mt-1 gps-mono text-[10px] tracking-[0.15em] flex gap-3">
                      <span className="text-cyan-400">{u.public_id || "—"}</span>
                      <span className="text-mute truncate">{u.email}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => addAthleteToRoster(u)}
                    className="gps-btn ghost !px-2"
                    disabled={adding === u.id}
                    data-testid={`admin-scoring-add-${u.id}`}
                    title={`Add ${u.name} to this event`}
                    aria-label={`Add ${u.name}`}
                  >
                    <UserPlus size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Scoring form */}
          <div>
            {!selected ? (
              <div className="p-8 text-center text-mute gps-mono text-xs tracking-[0.2em]">
                SELECT AN ATHLETE ON THE LEFT TO SCORE
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div>
                    <div className="text-white gps-heading text-xl">{selected.user.name.toUpperCase()}</div>
                    <div className="text-mute gps-mono text-[10px] tracking-[0.2em] mt-1">
                      {selected.user.gender || "—"} · {selected.user.age ? `AGE ${selected.user.age}` : "AGE —"} · {selected.user.primary_sport || "NO SPORT"}
                    </div>
                  </div>
                  {staged && (
                    <div className="text-right">
                      <div className="gps-label">STAGED GPS</div>
                      <div className={`big-numeric text-3xl ${staged.committed ? "text-emerald-400" : "text-yellow-300"}`}
                        data-testid="admin-scoring-staged-gps"
                      >
                        {Number(staged.result?.gps ?? 0).toFixed(1)}
                      </div>
                    </div>
                  )}
                </div>

                {committedLocked && (
                  <div className="mb-3 text-emerald-400 gps-mono text-[11px] tracking-[0.2em]">
                    ● COMMITTED · edits disabled from here
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="gps-label">HEIGHT (CM)</label>
                    <input
                      className="gps-input"
                      type="number"
                      min="80" max="250" step="any"
                      value={form.height_cm}
                      disabled={committedLocked}
                      onChange={(e) => setField("height_cm", e.target.value)}
                      data-testid="admin-scoring-height"
                    />
                  </div>
                  <div>
                    <label className="gps-label">WEIGHT (KG)</label>
                    <input
                      className="gps-input"
                      type="number"
                      min="15" max="250" step="any"
                      value={form.weight_kg}
                      disabled={committedLocked}
                      onChange={(e) => setField("weight_kg", e.target.value)}
                      data-testid="admin-scoring-weight"
                    />
                  </div>
                  {TESTS.map((t) => (
                    <div key={t.key}>
                      <label className="gps-label">{t.label} ({t.unit})</label>
                      <input
                        className="gps-input"
                        type="number"
                        step="any"
                        value={form[t.key]}
                        disabled={committedLocked}
                        onChange={(e) => setField(t.key, e.target.value)}
                        data-testid={`admin-scoring-input-${t.key}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex gap-2 flex-wrap">
                  <button
                    onClick={saveScore}
                    className="gps-btn"
                    disabled={busy || committedLocked}
                    data-testid="admin-scoring-save"
                  >
                    <Save size={14} /> {busy ? "SAVING…" : (staged ? "UPDATE SCORE" : "SAVE SCORE")}
                  </button>
                  {staged && !committedLocked && (
                    <button
                      onClick={deleteScore}
                      className="gps-btn ghost"
                      disabled={busy}
                      data-testid="admin-scoring-delete"
                    >
                      <Trash2 size={14} /> CLEAR STAGED
                    </button>
                  )}
                </div>

                {staged && (
                  <div className="mt-6 border-t border-white/10 pt-4">
                    <div className="gps-label mb-2 inline-flex items-center gap-2">
                      <Trophy size={12} /> PILLARS SNAPSHOT
                    </div>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      {["strength","speed","explosive","stamina","control"].map((p) => (
                        <div key={p} className="p-2 border border-white/10 rounded-sm">
                          <div className="gps-mono text-[10px] text-mute tracking-[0.2em]">{p.slice(0,3).toUpperCase()}</div>
                          <div className="big-numeric text-white mt-1">
                            {Number(staged.result?.pillars?.[p] ?? 0).toFixed(1)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        </div>
        </GlassPanel>
      </div>
    </div>
  );
}
